import React, {PureComponent} from 'react';
import root from 'window-or-global';
import cx from 'classnames';

import {getAssetPath} from '../../utils/DieselUtils';
import {breakpoints} from '../../constants';
import Observer from '@researchgate/react-intersection-observer';

import styles from './Picture.pcss';

const EMPTY_IMAGE =
    'data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw==';

interface Props {
    title: string;
    className: string;
    lazy: boolean;
    srcSet: Record<string, string>;
    /*
     * Reason why this is exposed is because the launcher seems to be a
     * scaled up version of the web version, so a different `rootMargin`
     * should be used in order to make lazy loading work as expected.
     */
    rootMargin?: string;
    alt: string; // TODO: rename as altText to leverage imageData
}

interface State {
    canLazyLoad?: boolean;
    hasBrowserSupportForObjectFit?: boolean;
    hasImageLoaded?: boolean;
}

class Picture extends PureComponent<Props, State> {
    static defaultProps: Omit<Props, 'srcSet'> = {
        alt: '',
        className: '',
        lazy: false,
        rootMargin: '50%',
        title: ''
    };

    state: State = {
        canLazyLoad: false,
        hasImageLoaded: false,
        hasBrowserSupportForObjectFit: true
    };

    isMounted: boolean = false;

    componentDidMount() {
        this.isMounted = true;
        const hasBrowserSupportForObjectFit =
            root.hasOwnProperty('document') && 'object-fit' in root.document.body.style;

        this.setState({hasBrowserSupportForObjectFit});
    }

    componentWillUnmount() {
        this.isMounted = false;
    }

    sortBreakpoints = breaks =>
        breaks.sort((bp1, bp2) => breakpoints.value[bp2] - breakpoints.value[bp1]);

    // Used mostly for IE 11 which doesn't support 'object-fit' nor 'picture' reliably
    renderSpanTag = (path): React.ReactNode => {
        const {title, className} = this.props;
        const src: string = getAssetPath(path);

        const fillDimensions = {
            width: '100%',
            height: '100%'
        };

        // Uses inline styles to "force" them to take precedence
        return (
            <div style={fillDimensions}>
                <span
                    data-image={src}
                    className={className}
                    aria-label={title}
                    role="img"
                    style={{
                        display: 'inline-block',
                        backgroundImage: `url("${src}")`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        ...fillDimensions
                    }}
                />
            </div>
        );
    };

    setImageRef = (target): void => {
        if (!target) return;
        target.onload = this.handleImageLoad;
        if (target.complete) this.handleImageLoad();
    };

    handleImageLoad = (): void => {
        if (this.isMounted) {
            this.setState({hasImageLoaded: true});
        }
    };

    renderImgTag = (path): React.ReactNode => {
        const {className, lazy, title} = this.props;
        const {canLazyLoad, hasBrowserSupportForObjectFit, hasImageLoaded} = this.state;

        if (!path) return null;

        const imageUrl = getAssetPath(path);

        if (!hasBrowserSupportForObjectFit) return this.renderSpanTag(imageUrl);

        const classNames = cx(styles.image, className, {
            [styles.idle]: lazy && !hasImageLoaded,
            [styles.visible]: hasImageLoaded
        });

        const src = !lazy || canLazyLoad ? imageUrl : EMPTY_IMAGE;
        const ref = lazy && canLazyLoad ? this.setImageRef : null;

        return <img alt={title} className={classNames} src={src} ref={ref} data-image={src} />;
    };

    renderSources = (descendingBreakpoints): React.ReactNodeArray =>
        descendingBreakpoints.map((bp, key) => {
            const {lazy} = this.props;
            const {canLazyLoad} = this.state;
            const src = this.props.srcSet[bp];
            const minWidth = breakpoints.value[bp] ? `${breakpoints.value[bp]}px` : 0;

            if (!src) return null;

            const srcSet = !lazy || canLazyLoad ? getAssetPath(src) : EMPTY_IMAGE;
            const media = `(min-width: ${minWidth})`;

            return <source key={key} media={media} srcSet={srcSet} data-image={srcSet} />;
        });

    handleChange = ({isIntersecting}): void =>
        this.setState({canLazyLoad: isIntersecting || this.state.hasImageLoaded});

    render() {
        const {srcSet, lazy, rootMargin} = this.props;
        const {hasBrowserSupportForObjectFit, canLazyLoad} = this.state;
        const observerSettings = {
            disabled: lazy && canLazyLoad,
            onChange: this.handleChange,
            rootMargin
        };

        const breaks = Object.keys(srcSet);

        if (breaks.length === 1) {
            /**
             * `IntersectionObserver`'s HOC requires that a node wraps what
             * you're trying to render, thus the `<div>`.
             */
            return (
                <Observer {...observerSettings}>
                    <div className={styles.small}>{this.renderImgTag(srcSet[breaks[0]])}</div>
                </Observer>
            );
        }

        const descendingBreakpoints = this.sortBreakpoints(breaks);
        const fallbackSrc = srcSet[descendingBreakpoints[0]];

        if (!hasBrowserSupportForObjectFit) return this.renderSpanTag(fallbackSrc);

        /**
         * We use `fullSize` in the `picture` element so `IntersectionObserver`
         * can properly determine when it's in the viewport.
         */
        return (
            <Observer {...observerSettings}>
                <picture className={styles.fullSize}>
                    {this.renderSources(descendingBreakpoints)}
                    {this.renderImgTag(fallbackSrc)}
                </picture>
            </Observer>
        );
    }
}

export default Picture;
